import React, { useState, useEffect, useMemo } from 'react';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import useVehicleRegistrationFormData from './hook/useVehicleRegistrationFormData';
import VehicleRegistrationFormTable from './component/VehicleRegistrationFormTable';
import VehicleRegistrationFilters from './component/VehicleRegistrationFilters';
import VehicleRegistrationHistoryDialog from './components/VehicleRegistrationHistoryDialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useLazyExportVehicleRegistrationToExcelQuery } from '../../../../api/endpoints/vehicleRegistrationForm.js';
import GenericFileExportButton from '../../../../components/common/GenericFileExportButton.jsx';
import { CVRFStatus } from './constants/cvrfStatus.js';

const TAB_STATUS_MAP = {
    0: CVRFStatus.Draft, // Taslak
    1: CVRFStatus.SentForApproval, // Onay Bekleyenler
    2: CVRFStatus.Return, // İade Edilenler
    3: CVRFStatus.Approved // Onaylananlar
};

const DealerVehicleRegistrationFormPage = () => {
    const navigate = useNavigate();
    const { th } = useLanguage();
    const { apiVehicleRegistrationForm, filters, filterFields, handleSearch, handleApplyFilters, handleResetFilters, CVRFStatus, isLoading, isFetching, error, refetch, setStatusFilter, onPage, onSort, tabCounts,totalRecords, lazyParams, statusFilter, dealerOptions, queryParams} =
        useVehicleRegistrationFormData();

    // Custom filter types for GenericTableFilter
    const customFilterTypes = useMemo(
        () => ({
            startDate: 'date',
            endDate: 'date',
            dealerId: 'dropdown'
        }),
        []
    );

    const customFilterOptions = useMemo(() => ({
        dealerId: dealerOptions
    }), [dealerOptions]);

    const [activeIndex, setActiveIndex] = useState(0);

    const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
    const [currentHistoryRecord, setCurrentHistoryRecord] = useState(null);

    const handleTabChange = (e) => {
        const newIndex = e.index;
        setActiveIndex(newIndex);
        const newStatus = TAB_STATUS_MAP[newIndex];
        setStatusFilter(newStatus);

        handleResetFilters();
    };

    useEffect(() => {
        refetch();
    }, [statusFilter, refetch]);

    const handleCreateClaim = () => {
        navigate('/warranty-management/warranty-process-forms/dealer-vehicle-registration-form/create');
    };

    const handleShowHistory = (record) => {
        setCurrentHistoryRecord(record);
        setHistoryDialogVisible(true);
    };

    const hideHistoryDialog = () => {
        setHistoryDialogVisible(false);
        setCurrentHistoryRecord(null);
    };

    const tabLabels = [
        { label: th('warranty.dealerVehicleRegistrationForm.tabs.draft'), icon: 'pi pi-file-edit', color: 'orange', status: CVRFStatus.Draft },
        { label: th('warranty.dealerVehicleRegistrationForm.tabs.pendingApproval'), icon: 'pi pi-clock', color: 'blue', status: CVRFStatus.SentForApproval },
        { label: th('warranty.dealerVehicleRegistrationForm.tabs.returned'), icon: 'pi pi-replay', color: 'red', status: CVRFStatus.Return },
        { label: th('warranty.dealerVehicleRegistrationForm.tabs.approved'), icon: 'pi pi-check-circle', color: 'green', status: CVRFStatus.Approved }
    ];

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1>{th('warranty.dealerVehicleRegistrationForm.title')}</h1>
                <div className="flex gap-2">
                    <GenericFileExportButton
                        useExportQuery={useLazyExportVehicleRegistrationToExcelQuery}
                        exportParams={queryParams}
                        buttonLabel={th('common.export')}
                        buttonClassName="p-button-success"
                        icon="pi pi-file-excel"
                        defaultFileName="AracTescilFormu"
                    />
                    <Button label={th('warranty.dealerVehicleRegistrationForm.buttons.new')} icon="pi pi-plus" onClick={handleCreateClaim} />
                </div>
            </div>
            <div className="mb-2">
                <VehicleRegistrationFilters
                    filterFields={filterFields}
                    handleSearch={handleSearch}
                    handleApplyFilters={handleApplyFilters}
                    handleResetFilters={handleResetFilters}
                    customFilterTypes={customFilterTypes}
                    customFilterOptions={customFilterOptions}
                />
            </div>
            <TabView activeIndex={activeIndex} onTabChange={handleTabChange} className="vaf-tabs">
                {tabLabels.map((tab, index) => (
                    <TabPanel
                        key={index}
                        header={
                            <span className="tab-header">
                                <i className={`${tab.icon} mr-2`}></i>
                                {tab.label}
                                {tabCounts[tab.status] > 0 && <span className={`ml-2 px-2 py-1 border-round text-xs font-semibold bg-${tab.color}-100 text-${tab.color}-800`}>{tabCounts[tab.status]}</span>}
                            </span>
                        }
                        className="tab-content"
                    >
                        <VehicleRegistrationFormTable
                            vehicleRegistrationForms={apiVehicleRegistrationForm}
                            loading={isLoading || isFetching}
                            CVRFStatus={CVRFStatus}
                            onPage={onPage}
                            onSort={onSort}
                            lazyParams={lazyParams}
                            totalRecords={totalRecords}
                            tabLabel={tab.label}
                            currentStatus={tab.status}
                            handleShowHistory={handleShowHistory}
                        />
                    </TabPanel>
                ))}
            </TabView>

            <VehicleRegistrationHistoryDialog dialogVisible={historyDialogVisible} hideDialog={hideHistoryDialog} vehicleRegistrationForm={currentHistoryRecord} />
        </div>
    );
};

export default DealerVehicleRegistrationFormPage;
